import { getSchedaSteps, type ISchedaStep } from './scheda-steps.helper';
import type {
  IAttachment,
  ILookupImpianto,
  IParticipant,
  IQuestion,
  IScheda,
  Percorso,
  PercorsoIntervista,
  SessionMode,
} from '../models';

export interface IBuildTranscriptArgs {
  sessionMode: SessionMode;
  arch: string;
  somministratore: string;
  date: string;
  perc: Percorso;
  dur: string;
  parts: IParticipant[];
  questions: IQuestion[];
  answersDoc: string;
  answersFile: IAttachment | null;
}

// Trascrizione del Conduttore, per domanda (o testo risposte caricate nel percorso documento).
export const buildTranscript = (a: IBuildTranscriptArgs): string => {
  const names = a.parts.map((p) => p.name).join(', ');
  if (a.sessionMode === 'document') {
    let h = `PROFILO ABITATIVO — RISPOSTE CARICATE — CASÂBITO®\n`;
    h += `Architetto: ${a.arch || '—'} | Somministratore: ${a.somministratore || '—'} | Data: ${a.date || '—'} | Percorso: ${a.perc || '—'}\n`;
    h += `Partecipanti: ${names}\n\n`;
    const body =
      a.answersDoc.trim() ||
      (a.answersFile
        ? `(Le risposte del cliente sono fornite nel documento allegato: ${a.answersFile.name})`
        : '(Nessuna risposta fornita.)');
    return h + body;
  }
  let txt = `INTERVISTA PSICO-ABITATIVA — CASÂBITO®\n`;
  txt += `Architetto: ${a.arch || '—'} | Somministratore: ${a.somministratore || '—'} | Data: ${a.date || '—'} | Percorso: ${a.perc || '—'} | Durata: ${a.dur || '—'}\n`;
  txt += `Partecipanti: ${names}\n\n`;
  a.questions.forEach((q, qi) => {
    txt += `${'─'.repeat(50)}\nDOMANDA ${qi + 1}: "${q.text}"\n${'─'.repeat(50)}\n\n`;
    a.parts.forEach((p) => {
      const ans = p.answers[qi];
      if (!ans) {
        return;
      }
      txt += `[${p.name.toUpperCase()}]\n`;
      if (ans.transcript?.trim()) {
        txt += `Verbale: ${ans.transcript.trim()}\n`;
      }
      if (ans.notes?.trim()) {
        txt += `Note: ${ans.notes.trim()}\n`;
      }
      ans.subAnswers?.forEach((sa, si) => {
        // Scelte per immagine (card D8, tavole D9): "significato: Sì/No" nell'ordine della card.
        const vc = sa.visualChoices ?? {};
        const scelte = (q.subImages?.[si] ?? [])
          .filter((img) => undefined !== vc[img.id])
          .map((img) => `${img.meaning}: ${'si' === vc[img.id] ? 'Sì' : 'No'}`);
        if (!sa.transcript?.trim() && 0 === scelte.length) {
          return;
        }
        txt += `  · Spunto ${q.subCodes?.[si] ?? si + 1} "${q.sondaggi[si]}"\n`;
        if (sa.transcript?.trim()) {
          txt += `    Risposta: ${sa.transcript.trim()}\n`;
        }
        if (scelte.length) {
          txt += `    Immagini: ${scelte.join(', ')}\n`;
        }
      });
      txt += '\n';
    });
  });
  return txt;
};

const PRIORITA_LABELS: Record<string, string> = {
  altezze: 'Altezze interne reali, ambiente per ambiente',
  partizioni: 'Natura partizioni (portanti/non) e solette',
  scarichi: 'Posizione scarichi a pavimento (cucina e bagni)',
  aperture: 'Conformità urbanistica per nuove aperture/lucernari',
  impianti: 'Stato impianti (elettrico, idrico, termico)',
  cantina: 'Cantina: altezza, umidità, accessibilità',
  orientamento: 'Orientamento casa e giardino',
  vicinato: 'Vincoli di vicinato / condominiali',
};

// Scheda Abitativa → testo markdown per il prompt.
// impiantiLookup: nomi di impianti e opzioni (select_impianto[_opzione]); senza lookup si
// stampano i codici, la riga non si perde.
export const buildSchedaText = (s: IScheda, perc: Percorso, impiantiLookup: readonly ILookupImpianto[] = []): string => {
  const steps: ISchedaStep[] = getSchedaSteps(s);
  const stepNumber = (value: number): number => steps.findIndex((step) => step.value === value) + 1;
  const d = s.drv || {};
  const mk = (k: string): string => (d[k] ? ` [${d[k]}]` : '');
  const ln = (label: string, k: keyof IScheda): string => {
    const v = s[k];
    return v && String(v).trim() ? `- ${label}: ${String(v).trim()}${mk(k as string)}\n` : '';
  };

  let t = '';
  t += `- Percorso: ${perc === 'A' ? 'A — casa esistente da ristrutturare' : perc === 'B' ? 'B — acquisto / casa non ancora abitata' : 'non indicato'}\n\n`;

  let s1 = '';
  s1 += ln('Tipologia', 'tipologia');
  s1 += ln("Stato dell'intervento", 'statoIntervento');
  s1 += ln('Epoca / anno', 'epoca');
  s1 += ln('Numero livelli', 'livelli');
  s1 += ln('Cantina / sottotetto', 'cantina');
  s1 += ln('Superficie totale utile (mq)', 'superficie');
  if (s1) {
    t += `**1 · L'abitazione in sintesi**\n${s1}\n`;
  }

  const amb = (s.ambienti || []).filter(
    (a) =>
      a.tipo || a.nome || a.note || a.openSpace || a.coinvolto,
  );
  if (amb.length) {
    t += `**2 · Rilievo per ambiente**\n`;
    amb.forEach((a) => {
      const label = [a.tipo, a.nome].filter(Boolean).join(' — ') || '(ambiente)';
      const openSpace = a.openSpace ? ', open space' : '';
      // Perimetro dell'intervento (blocco B): quali stanze l'intervento tocca davvero. Il modello
      // deve poter distinguere una stanza rilevata da una stanza in cui si lavora.
      const coinvolto = a.coinvolto ? ' [COINVOLTO NEI LAVORI]' : '';
      t += `- ${label}${coinvolto}${openSpace}${a.note ? ', note: ' + a.note : ''}\n`;
    });
    t += '\n';
  }

  let s3 = '';
  s3 += ln('Esposizione dei fronti', 'esposizioneFronti');
  s3 += ln('Ore di sole diretto', 'oreSole');
  s3 += ln('Ambienti più bui', 'ambientiBui');
  s3 += ln('Aperture esistenti', 'aperture');
  s3 += ln('Possibilità nuove aperture', 'nuoveAperture');
  if (s3 && 0 < stepNumber(3)) {
    t += `**${stepNumber(3)} · Luce e orientamento**\n${s3}\n`;
  }

  let s4 = '';
  s4 += ln('Muri portanti / tramezzi', 'muri');
  s4 += ln('Scarichi cucina', 'scarichiCucina');
  s4 += ln('Scarichi bagni', 'scarichiBagni');
  s4 += ln(
    'Tipo Caldaia / terminali di erogazione / termostato ambiente generale o per stanza',
    'caldaia',
  );
  s4 += ln('Impianto elettrico', 'elettrico');
  s4 += ln('Impianto idraulico', 'idraulico');
  // Perimetro (blocco B): cosa si RIFÀ, con l'eventuale tipo di intervento scelto.
  const rifatti = (s.impianti ?? []).map((code) => {
    const l = impiantiLookup.find((i) => i.code === code);
    const opz = s.impiantiOpzioni?.[code];
    const opzName = l?.opzioni.find((o) => o.code === opz)?.name ?? opz;
    return `${l?.name ?? code}${opzName ? ` (${opzName})` : ''}`;
  });
  if (rifatti.length) {
    s4 += `- Impianti da sostituire/adeguare: ${rifatti.join(', ')}\n`;
  }
  if (s.noteLibere?.trim()) {
    s4 += `- Note libere: ${s.noteLibere.trim()}\n`;
  }
  if (s4) {
    t += `**${stepNumber(4)} · Strutture e impianti**\n${s4}\n`;
  }

  let s5 = '';
  s5 += ln('Giardino', 'giardino');
  s5 += ln('Portico', 'terrazzo');
  s5 += ln('Rapporto dentro-fuori', 'rapportoDentroFuori');
  if (s5 && 0 < stepNumber(5)) {
    t += `**${stepNumber(5)} · Esterni e contesto**\n${s5}\n`;
  }

  let s6 = '';
  s6 += ln('Contesto', 'contesto');
  s6 += ln('Rumori molesti esterni', 'rumoriMolestiEsterni');
  s6 += ln('Vicini rumorosi', 'viciniRumorosi');
  s6 += ln("Qualità dell'aria", 'qualitaAria');
  s6 += ln('Vi è privacy', 'viPrivacy');
  if (s6 && 0 < stepNumber(6)) {
    t += `**${stepNumber(6)} · Contesto**\n${s6}\n`;
  }

  let s7 = '';
  if (s.vincoliFissi?.trim()) {
    s7 += `- Fissi: ${s.vincoliFissi.trim()}\n`;
  }
  if (s.vincoliSemifissi?.trim()) {
    s7 += `- Semi-fissi: ${s.vincoliSemifissi.trim()}\n`;
  }
  if (s.gradiLiberta?.trim()) {
    s7 += `- Gradi di libertà: ${s.gradiLiberta.trim()}\n`;
  }
  if (s7) {
    t += `**${stepNumber(7)} · Vincoli e gradi di libertà**\n${s7}\n`;
  }

  const prSel = Object.keys(PRIORITA_LABELS).filter((k) => s.priorita && s.priorita[k]);
  if (prSel.length || s.altrePriorita?.trim()) {
    t += `**${stepNumber(8)} · Priorità di sopralluogo**\n`;
    prSel.forEach((k) => {
      t += `- ${PRIORITA_LABELS[k]}\n`;
    });
    if (s.altrePriorita?.trim()) {
      t += `- Altre: ${s.altrePriorita.trim()}\n`;
    }
    t += '\n';
  }

  const out = t.trim();
  return out || "Scheda dell'Abitazione non compilata.";
};

// ── Perimetro dell'intervento → blocco per i prompt (Tre Profondità Rev. 1 §11, blocco G) ──
// I tre dati che hanno deciso il percorso — stato dell'intervento, ambienti coinvolti, impianti
// da sostituire — più il percorso con cui l'intervista è partita. Il Prompt 1 ne ricava il
// perimetro delle domande somministrate, il Prompt 2 usa gli impianti come leve e vincoli.
// Blocco a sé e non dentro buildSchedaText: con la Scheda caricata come documento quel testo non
// viene generato affatto, mentre il perimetro c'è comunque (lo fissa l'avvio dell'intervista).
export interface IPerimetroArgs {
  // Percorso con cui l'intervista è partita. null = intervista non ancora avviata (flusso
  // documento, o profilo generato prima delle Tre Profondità): il blocco non si scrive.
  percorso: PercorsoIntervista | null;
  statoIntervento: string;
  // Nomi leggibili, non codici: è il testo che legge il modello.
  ambientiCoinvolti: string[];
  // opzione = etichetta del tipo di intervento scelto nella Scheda, se l'impianto ne ha una.
  impianti: { name: string; opzione?: string }[];
}

export const buildPerimetroText = (p: IPerimetroArgs): string | null => {
  if (null === p.percorso) {
    return null;
  }
  let t = `- Percorso dell'intervista: ${p.percorso}\n`;
  t += `- Stato dell'intervento: ${p.statoIntervento.trim() || 'non indicato'}\n`;
  t += `- Ambienti coinvolti nei lavori: ${p.ambientiCoinvolti.length ? p.ambientiCoinvolti.join(', ') : 'nessuno indicato'}\n`;
  const imp = p.impianti.map((i) => `${i.name}${i.opzione ? `: ${i.opzione}` : ''}`);
  t += `- Impianti da sostituire/adeguare: ${imp.length ? imp.join(', ') : 'nessuno'}\n`;
  return t;
};
